// Types + formatters for the 3M Deviation Summary report.
//
// Formatters are RE-EXPORTED from threeMShared, never re-implemented — the
// deviation sign convention (U+2212 minus outside the ₹, red over / green
// under) is shared with the 3M Consolidation and Monthly Execution reports and
// must not drift.
//
// `groupOf` is used below by TAB_META as well as re-exported, so its import
// sits here at the top — `export … from` counts as module body, and eslint's
// import/first fires on any import that follows one.
import { groupOf } from "../shared/normGroupLabels";
// `export … from` re-exports do NOT create a local binding, so anything this
// module USES (rather than merely forwards) needs its own import.
import { num } from "../ThreeMConsolidation/threeMShared";

export {
  num,
  fmtINR,
  fmtINRCompact,
  fmtQty,
  fmtINRSigned,
  fmtDevPct,
} from "../ThreeMConsolidation/threeMShared";

export type DeviationTabKey = "MATERIAL" | "MANPOWER" | "MACHINERY";

// Every rate/money field is a STRING and an EMPTY / MISSING value means "not
// measurable" — the backend never sends '0.00' for an absent rate, because a
// fake zero reads as "on target" (or as -100% once divided). gRPC also omits
// the default '', so these arrive as undefined. Treat both as "no value".
export interface DeviationRow {
  source: string; // MATERIAL | PRW | OTHERS | NORM | TOOLS | EQUIPMENT
  materialId?: number;
  manpowerNormId?: number;
  laborTypeId?: number;
  laborCategoryId?: number;
  machineryNormId?: number;
  toolId?: number;
  deploymentNormId?: number;
  workUomId?: number;
  itemName?: string;
  itemCode?: string;
  /** BOQ item title for this row's own code: 'B3.1.1' -> 'Class I, II, & III'. */
  itemTitle?: string;
  uomName?: string;
  head?: string;
  /** The activity the machinery NORM MASTER is scoped to — machinery_norms
   *  holds one row per BOQ code × activity, so this is what tells otherwise
   *  identically-named siblings apart. NOT a `breakdown` activity. */
  normActivityId?: number;
  normActivityName?: string;
  categoryId?: number;
  categoryName?: string;
  plannedQuantity?: string;
  plannedCost?: string;
  boqRate?: string;
  targetRate?: string;
  actualRate?: string;
  targetSource?: string; // NORM | MASTER | ''
  actualSource?: string; // INVENTORY | TIMELOG | ''
  deviation?: string;
  deviationPct?: string;
  impactValue?: string;
  targetDeviation?: string;
  targetDeviationPct?: string;
  targetImpactValue?: string;
  actualQuantity?: string;
  targetCoverageQuantity?: string;
  taskCount?: number;
  lineCount?: number;
  rateBasis?: string; // 'LIVE' on equipment rows
  // Norm group, derived server-side from the row's own BOQ code. Absent on
  // every row with no norm code — manpower OTHERS, machinery TOOLS, and all
  // material rows (a material's grain is the material, not the norms that
  // price it), which is why the Material tab keeps its category grouping.
  level1Code?: string; // 'B3'
  level1Name?: string; // 'Underground Excavation'
  groupCode?: string; // 'B3.2', or 'B3.3(1)' when the BOQ discriminates
  groupName?: string; // 'Excavation of all Hydraulic tunnels'
  // Where this resource's cost actually sits — the activities, then the tasks,
  // it was applied to. MANPOWER and MACHINERY only; material stays flat (a
  // material's grain is the material, which no single norm owns).
  breakdown?: DeviationActivityNode[];
}

// One activity / task under a register row. Same money-and-rate vocabulary as
// the row, so the table renders drill rows with the SAME cell renderers and the
// same '' = "not measurable" rule.
//
// boqRate varies per task (that task's own planned rate — the reason the drill
// exists); targetRate is inherited from the row, because a norm master prices
// the RESOURCE, not the face; actualRate exists only where the grain has
// time-log capture (manpower OTHERS, machinery TOOLS).
export interface DeviationTaskNode {
  taskId?: number;
  taskName?: string;
  taskSequenceId?: string;
  plannedQuantity?: string;
  plannedCost?: string;
  boqRate?: string;
  targetRate?: string;
  actualRate?: string;
  deviation?: string;
  deviationPct?: string;
  impactValue?: string;
  targetDeviation?: string;
  targetDeviationPct?: string;
  targetImpactValue?: string;
  actualQuantity?: string;
  lineCount?: number;
}

export interface DeviationActivityNode
  extends Omit<DeviationTaskNode, "taskId" | "taskName" | "taskSequenceId"> {
  activityId?: number;
  activityName?: string;
  phaseId?: number;
  phaseName?: string;
  taskCount?: number;
  tasks?: DeviationTaskNode[];
}

// The machinery tab's cost split. Every machinery row belongs to exactly ONE
// source, so a row's own cost lands in exactly one column and the three columns
// stay additive at every level — which is what makes a group (and the TOTAL)
// show mucking AND equipment side by side instead of only the norm-priced half.
export type MachineryCostSplit = {
  muckingCost: number;
  toolsCost: number;
  equipmentCost: number;
};

export const machinerySplit = (row: DeviationRow): MachineryCostSplit => {
  const cost = num(row.plannedCost);
  return {
    muckingCost: row.source === "NORM" ? cost : 0,
    toolsCost: row.source === "TOOLS" ? cost : 0,
    equipmentCost: row.source === "EQUIPMENT" ? cost : 0,
  };
};

export interface DeviationTab {
  tab?: string;
  rowCount?: number;
  plannedCost?: string;
  impactValue?: string;
  targetImpactValue?: string;
  measuredRowCount?: number;
  measuredPlannedCost?: string;
  targetedRowCount?: number;
  rows?: DeviationRow[];
}

export interface DeviationSummaryData {
  projectName?: string;
  material?: DeviationTab;
  manpower?: DeviationTab;
  machinery?: DeviationTab;
}

/** Present = the backend could measure it. '' / undefined = it could not. */
export const has = (value?: string): boolean =>
  value !== undefined && value !== null && value !== "";

// Norm-group labels. `B3 — Underground Excavation` reads as the group; rows
// without a norm code fall back to the tab's original label rather than being
// swept into a meaningless "Ungrouped" bucket.
//
// Defined in reports/shared/normGroupLabels and re-exported here: the Manpower
// Cost Analysis register buckets the same manpower rows, and the two pages must
// not drift on either the label format or the B9-before-B10 sort.
export { groupOf, compareGroupLabels } from "../shared/normGroupLabels";

// The BOQ code a row was priced under, as its own grouping level: 'B3.1.1 —
// Class I, II, & III'.
//
// This level exists because a norm master is NOT one row per code. machinery_
// norms holds one row per code × activity — B3.1.1 is 10 separate norms on
// URI-1, one per adit, each at its own rate — and the deployment (equipment)
// norm for the same code is an eleventh row. Without this level the register
// shows eleven identically-labelled siblings and no total for the BOQ line
// anyone actually gets paid against. With it, the code node totals mucking AND
// equipment for that line, which is the number that matters.
export const codeOf = (row: DeviationRow): string | null =>
  row.itemCode
    ? [row.itemCode, row.itemTitle?.replace(/^[-\s]+/, "")]
        .filter(Boolean)
        .join(" — ")
    : null;

export const TAB_META: Record<
  DeviationTabKey,
  {
    label: string;
    nameHeader: string;
    /** Top grouping level. */
    groupLabel: (row: DeviationRow) => string;
    /**
     * Optional second level, nested under the first. Machinery is the reason
     * this exists: every machinery norm on a tunnelling project sits under
     * B3, so a single level would just reproduce the flat list.
     */
    subGroupLabel?: (row: DeviationRow) => string | null;
    /**
     * Optional third level: the row's own BOQ code. Nested only where it
     * actually separates things — see the nesting rule in
     * DeviationSummaryTable, which drops any level that would just wrap every
     * item in a group of one (manpower codes are unique on URI-1, so the
     * manpower tab silently skips this level).
     */
    codeLabel?: (row: DeviationRow) => string | null;
  }
> = {
  MATERIAL: {
    label: "Material",
    nameHeader: "Material Name",
    // Deliberately NOT norm groups: material rows are materials, and one
    // material is priced by up to 21 norms.
    groupLabel: (row) => row.categoryName || "Uncategorised",
  },
  MANPOWER: {
    label: "Manpower",
    nameHeader: "Labour Item",
    groupLabel: (row) =>
      groupOf(row.level1Code, row.level1Name) ??
      // Mirrors the Manpower Cost Analysis split for un-normed rows.
      (row.source === "PRW" ? "PRW / Sub-Contract" : "Others (Task Resources)"),
    subGroupLabel: (row) => groupOf(row.groupCode, row.groupName),
    codeLabel: codeOf,
  },
  MACHINERY: {
    label: "Machinery",
    nameHeader: "Machinery Item",
    groupLabel: (row) =>
      groupOf(row.level1Code, row.level1Name) ??
      (row.head === "TOOLS"
        ? "Tools & Machinery"
        : row.head === "EQUIPMENT"
          ? "Equipment Norms"
          : row.head || "Machinery Norms"),
    subGroupLabel: (row) => groupOf(row.groupCode, row.groupName),
    codeLabel: codeOf,
  },
};

// Why a rate column is blank, in the row's own terms. Drives the tooltip on
// every em dash so an unmeasurable cell explains itself instead of looking
// like missing data.
export const explainTarget = (row: DeviationRow): string => {
  if (has(row.targetRate)) {
    return row.targetSource === "MASTER"
      ? "From the material master's list price (no norm links this material yet)"
      : "Current norm-master rate";
  }
  if (row.source === "OTHERS") {
    return "No target: labour types/categories have no rate master — only rate cards, which are not linked at this grain";
  }
  if (row.source === "TOOLS") {
    return "No target: the tools & machinery master carries no rate";
  }
  if (row.rateBasis === "LIVE") {
    return "Live-rated: equipment cost is recomputed each request from the rate version in force, so there is no separate target";
  }
  return "No norm-master rate found for this item";
};

export const explainActual = (row: DeviationRow): string => {
  if (has(row.actualRate)) {
    return row.actualSource === "INVENTORY"
      ? "Site moving-average purchase rate (weighted across inward receipts)"
      : "Weighted average of approved time-log actuals";
  }
  if (row.source === "PRW") {
    return "No actuals: time logs record labour against task resource lines, not against manpower norms";
  }
  if (row.source === "NORM") {
    return "No actuals: time logs record machinery against task tool lines, not against machinery norms";
  }
  if (row.rateBasis === "LIVE") {
    return "No actuals: equipment deployment has no time-log capture";
  }
  if (row.source === "MATERIAL") {
    return "No actuals: this material has no inward stock at the project's site yet";
  }
  return "No approved time-log actuals for this item yet";
};
